import { Fragment, memo, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { QuestionCard } from '@components/QuestionCard';
import { QuestionCardSkeleton } from '@components/QuestionCardSkeleton';
import type { HomeFeedProps } from '../types';

const LOADING_SKELETON_COUNT = 3;

/**
 * Rendered list position of `injectedItem` (recap/pill redesign §2: the Home
 * recap section sits INSIDE the feed at ~index 3–4 — here, the 4th visible
 * item). Shorter feeds clamp to the end so the item never disappears just
 * because the feed is quiet.
 */
const INJECTED_ITEM_INDEX = 3;

/**
 * Reserved sibling key for the injected item. Question cards are keyed by
 * UUID, so this can never collide; keyed independently, the injected subtree
 * keeps its state while feed prepends/pagination shift the questions around
 * it (React reconciles keyed siblings by key, not position).
 */
const INJECTED_ITEM_KEY = 'injected-item-slot';

/**
 * Feed list component with infinite scroll
 *
 * Best practices applied:
 * - Wrapped with memo() to prevent unnecessary re-renders
 * - Named function for better React DevTools debugging
 * - Intersection observer for efficient infinite scroll
 * - Stable callback pattern for onLoadMore
 *
 * `injectedItem` interleaves as its OWN keyed list item (pure render-time —
 * a local splice into the mapped array, before JSX handoff): question keys
 * (`question.id`), `positionInFeed` (the question's index among QUESTIONS,
 * not rendered slots), and the pagination sentinel are untouched by the
 * injection.
 */
export const HomeFeed = memo(function HomeFeed({
  questions,
  isLoadingMore,
  hasNextPage,
  onLoadMore,
  onInteractionBlocked,
  returnTo,
  communityContext,
  injectedItem,
  helpableQuestionIds,
}: HomeFeedProps) {
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '400px',
  });

  // Trigger load more when bottom is visible
  useEffect(() => {
    if (inView && hasNextPage && !isLoadingMore) {
      onLoadMore();
    }
  }, [inView, hasNextPage, isLoadingMore, onLoadMore]);

  const items = questions.map((question, index) => (
    <QuestionCard
      key={question.id}
      question={question}
      positionInFeed={index}
      sourceContext="feed"
      onInteractionBlocked={onInteractionBlocked}
      returnTo={returnTo}
      communityContext={communityContext}
      // Marks helpable posts in place (spec P11) - the `questions` array order is
      // NEVER touched, so the goer sort-boost stays the sole ordering rule. A
      // non-helpable caller (no set) passes `undefined`, not an explicit `false`
      // - the prop is optional, so "not applicable" is absence, not a boolean.
      isHelpable={helpableQuestionIds?.has(question.id)}
    />
  ));
  if (injectedItem != null && questions.length > 0) {
    items.splice(
      Math.min(INJECTED_ITEM_INDEX, items.length),
      0,
      <Fragment key={INJECTED_ITEM_KEY}>{injectedItem}</Fragment>,
    );
  }

  return (
    <div className="flex flex-col gap-[15px]">
      {items}

      {/* Infinite scroll trigger */}
      {hasNextPage && (
        <div ref={loadMoreRef} className="py-4 flex justify-center">
          {isLoadingMore && (
            <div className="flex flex-col gap-[15px] w-full">
              {Array.from({ length: LOADING_SKELETON_COUNT }, (_, i) => (
                <QuestionCardSkeleton key={`loading-${i}`} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
});
